import { PermissionCode } from '@gb-ticket/shared-types';
import { BookOpenIcon, Loader2Icon } from 'lucide-react';
import { Link, useNavigate } from 'react-router';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { hasPermission } from '@/features/auth/permissions';
import { useMe } from '@/features/auth/use-me';
import { ApiRequestError } from '@/lib/api';
import { useDraftKbArticleFromTicket, useKbArticles } from '@/products/helpdesk/features/kb/use-kb';
import { TicketSectionCard } from '@/products/helpdesk/features/tickets/ticket-section-card';

export function TicketKbPanel({
  ticketId,
  ticketNumber,
}: {
  ticketId: string;
  ticketNumber: number;
}) {
  const me = useMe();
  const canWrite = hasPermission(me.data, PermissionCode.KB_WRITE);
  const canRead = hasPermission(me.data, PermissionCode.KB_READ);
  const articles = useKbArticles({
    page: 1,
    pageSize: 20,
    sortBy: 'updatedAt',
    sortOrder: 'desc',
    ticketId,
  });
  const draft = useDraftKbArticleFromTicket();
  const navigate = useNavigate();

  async function onDraft() {
    try {
      const article = await draft.mutateAsync({ ticketNumber });
      toast.success('KI-Entwurf liegt vor. Bitte Titel, Kurztext und Inhalt prüfen.');
      void navigate(`/helpdesk/wissen/${article.slug}`);
    } catch (error) {
      toast.error(
        error instanceof ApiRequestError
          ? error.message
          : 'Der Artikelentwurf konnte nicht erstellt werden.',
      );
    }
  }

  if (!canRead) {
    return null;
  }

  return (
    <TicketSectionCard
      tone="steel"
      icon={BookOpenIcon}
      collapsible
      defaultOpen={false}
      title="Wissensdatenbank"
      description="Artikel, die aus diesem Ticket entstanden sind."
    >
      <div className="space-y-3">
        {(articles.data?.items ?? []).map((article) => (
          <Link
            key={article.id}
            to={`/helpdesk/wissen/${article.slug}`}
            className="block text-sm text-primary hover:underline"
          >
            {article.title}
          </Link>
        ))}
        {articles.data?.items.length === 0 && (
          <p className="text-sm text-muted-foreground">Noch kein Artikel verknüpft.</p>
        )}
        {canWrite && (
          <Button
            type="button"
            size="sm"
            variant="outline"
            disabled={draft.isPending}
            onClick={() => void onDraft()}
          >
            {draft.isPending && <Loader2Icon className="animate-spin" aria-hidden />}
            Artikel aus Ticket anlegen
          </Button>
        )}
      </div>
    </TicketSectionCard>
  );
}
